import * as React from "react";
import { useUrlState } from "../../../Common";
import { usePlpDataSourceUrlState } from "../plp-data-source-url-state";
import { useProductListState_asController } from "./product-list-state-provider";

interface ProductListUrlStateConsumerInterface {
  hasInitializedQueryState: boolean;
  hasConsolidatedUrlStateDataSource: boolean;
}

const defaultValue = {} as unknown as ProductListUrlStateConsumerInterface;

const ProductListUrlStateContext = React.createContext(defaultValue);

interface ProductListUrlStateProviderProps {
  providerId: string;
}

export const ProductListUrlStateProvider: React.FunctionComponent<
  ProductListUrlStateProviderProps
> = (props) => {
  const thisProviderEventNameBase = `bbnt-products-list-url-state-provider-${props.providerId}`;

  const {
    setStartPage,
    setSelectedSortingOption,
    setFacetDisplayNames,
    setSelectedOptions,
  } = useProductListState_asController();

  const { hasInitializedQueryState, read: readUrlState } = useUrlState();
  const { plpStateUrlCached } = usePlpDataSourceUrlState();

  const [
    hasConsolidatedUrlStateDataSource,
    setHasConsolidatedUrlStateDataSource,
  ] = React.useState(false);

  React.useEffect(() => {
    if (!hasInitializedQueryState) {
      readUrlState();
    }
  }, [hasInitializedQueryState]);

  const consolidateUrlState = React.useCallback(() => {
    if (plpStateUrlCached?.startPage) {
      setStartPage(plpStateUrlCached.startPage);
    }
    if (plpStateUrlCached?.selectedSortingOption) {
      setSelectedSortingOption(plpStateUrlCached.selectedSortingOption);
    }
    if (plpStateUrlCached?.facets) {
      setFacetDisplayNames(plpStateUrlCached.facets);
    }
    if (plpStateUrlCached?.selectedOptions) {
      setSelectedOptions(plpStateUrlCached.selectedOptions);
    }
    setHasConsolidatedUrlStateDataSource(true);
  }, [plpStateUrlCached]);
  React.useEffect(() => {
    window.addEventListener(
      `${thisProviderEventNameBase}-url-state-consolidate`,
      consolidateUrlState
    );
    return () => {
      window.removeEventListener(
        `${thisProviderEventNameBase}-url-state-consolidate`,
        consolidateUrlState
      );
    };
  }, [plpStateUrlCached]);

  React.useEffect(() => {
    if (hasInitializedQueryState && !hasConsolidatedUrlStateDataSource) {
      window.dispatchEvent(
        new Event(`${thisProviderEventNameBase}-url-state-consolidate`)
      );
    }
  }, [hasInitializedQueryState, hasConsolidatedUrlStateDataSource]);

  const value: ProductListUrlStateConsumerInterface = React.useMemo(
    () => ({
      hasInitializedQueryState,
      hasConsolidatedUrlStateDataSource,
    }),
    [hasInitializedQueryState, hasConsolidatedUrlStateDataSource]
  );

  return (
    <ProductListUrlStateContext.Provider value={value}>
      {props?.children || []}
    </ProductListUrlStateContext.Provider>
  );
};

export const useProductListUrlState = () => {
  const context = React.useContext(ProductListUrlStateContext);
  if (context === defaultValue) {
    throw new Error(
      "useProductListUrlState must be used within a ProductListUrlStateProvider"
    );
  }
  return context;
};
